import { genId } from '@/utils/id'
import { toMinor } from '@/utils/format'
import {
  seedBudgets, seedExchangeRates, seedExpenses, seedRepayments
} from '@/mock/seed'

// 分帳金額一律以「結算幣別的最小單位整數」計算與儲存（expense.shares、repayment.amountMinor 皆是），
// 只有 expense.amountMinor 本身維持「原始輸入幣別」，方便畫面顯示「原始金額 + 換算後金額」兩者。
function splitEven (settlementMinor, memberIds, payerId) {
  const n = memberIds.length
  const even = Math.floor(settlementMinor / n)
  const remainder = settlementMinor - even * n
  // 除不盡的餘數歸給付款人；付款人不在分攤名單中則歸給名單中第一位（4.6.4）。
  const remainderTo = memberIds.includes(payerId) ? payerId : memberIds[0]
  return memberIds.map(memberId => ({
    memberId,
    amountMinor: even + (memberId === remainderTo ? remainder : 0)
  }))
}

function buildNetPairs (debts) {
  const pairKeys = new Set()
  Object.keys(debts).forEach(a => Object.keys(debts[a]).forEach(b => {
    pairKeys.add([a, b].sort().join('|'))
  }))
  const result = []
  pairKeys.forEach(key => {
    const [a, b] = key.split('|')
    const ab = (debts[a] && debts[a][b]) || 0
    const ba = (debts[b] && debts[b][a]) || 0
    const diff = ab - ba
    if (diff > 0) result.push({ from: a, to: b, amountMinor: diff })
    else if (diff < 0) result.push({ from: b, to: a, amountMinor: -diff })
  })
  return result
}

export default {
  namespaced: true,
  state: {
    budgets: seedBudgets,
    exchangeRates: seedExchangeRates,
    expenses: seedExpenses,
    repayments: seedRepayments
  },
  getters: {
    budgetsByTrip: state => tripId => state.budgets.filter(b => b.tripId === tripId),
    ratesByTrip: state => tripId => state.exchangeRates.filter(r => r.tripId === tripId),
    rateFor: (state, getters) => (tripId, currency) => {
      const found = getters.ratesByTrip(tripId).find(r => r.currency === currency)
      return found ? found.rate : 1
    },
    expensesByTrip: state => tripId => state.expenses.filter(e => e.tripId === tripId),
    visibleExpensesByTrip: (state, getters) => (tripId, viewerId) =>
      getters.expensesByTrip(tripId).filter(e => e.visibility !== 'private' || e.payerId === viewerId || e.createdBy === viewerId),
    repaymentsByTrip: state => tripId => state.repayments.filter(r => r.tripId === tripId),

    // 4.6.6 群收款視圖：單筆支出底下每個分攤者的還款狀態
    shareStatuses: (state, getters) => tripId => {
      const repayments = getters.repaymentsByTrip(tripId)
      return (expense) => expense.shares.map(share => {
        if (share.memberId === expense.payerId) {
          return { ...share, status: 'payer' }
        }
        const r = repayments.find(rp => rp.expenseId === expense.id && rp.fromId === share.memberId && rp.toId === expense.payerId)
        return { ...share, status: r ? r.status : 'unpaid' }
      })
    },

    // 4.6.6 一對一視圖：所有支出（分攤）與已確認還款相抵後，每兩人之間的淨額
    netPairs: (state, getters) => tripId => {
      const debts = {}
      getters.expensesByTrip(tripId).forEach(e => {
        if (e.splitMode === 'none') return
        e.shares.forEach(s => {
          if (s.memberId === e.payerId) return
          debts[s.memberId] = debts[s.memberId] || {}
          debts[s.memberId][e.payerId] = (debts[s.memberId][e.payerId] || 0) + s.amountMinor
        })
      })
      getters.repaymentsByTrip(tripId).filter(r => r.status === 'confirmed').forEach(r => {
        debts[r.fromId] = debts[r.fromId] || {}
        debts[r.fromId][r.toId] = (debts[r.fromId][r.toId] || 0) - r.amountMinor
      })
      return buildNetPairs(debts).filter(p => p.amountMinor > 0)
    },

    // 個人總覽：我的總花費（含個人支出）、我應付的、別人欠我的
    personalOverview: (state, getters) => (tripId, memberId) => {
      const expenses = getters.expensesByTrip(tripId)
      let mySpend = 0
      expenses.forEach(e => {
        if (e.splitMode === 'none') {
          if (e.payerId === memberId) mySpend += e.shares[0] ? e.shares[0].amountMinor : 0
          return
        }
        const mine = e.shares.find(s => s.memberId === memberId)
        if (mine) mySpend += mine.amountMinor
      })
      const nets = getters.netPairs(tripId)
      const iOwe = nets.filter(p => p.from === memberId).reduce((sum, p) => sum + p.amountMinor, 0)
      const owedToMe = nets.filter(p => p.to === memberId).reduce((sum, p) => sum + p.amountMinor, 0)
      return { mySpendMinor: mySpend, iOweMinor: iOwe, owedToMeMinor: owedToMe }
    },

    // 4.6.2 預算 vs 實際支出（依類別，皆換算為結算幣別）
    budgetVsActual: (state, getters) => tripId => {
      const budgets = getters.budgetsByTrip(tripId)
      const expenses = getters.expensesByTrip(tripId).filter(e => e.visibility !== 'private')
      return budgets.map(b => {
        const actualMinor = expenses
          .filter(e => e.category === b.category)
          .reduce((sum, e) => sum + e.shares.reduce((s, sh) => s + sh.amountMinor, 0), 0)
        return { category: b.category, budgetMinor: toMinor(b.amount), actualMinor }
      })
    }
  },
  mutations: {
    ADD_EXPENSE (state, expense) {
      state.expenses.unshift(expense)
    },
    UPDATE_EXPENSE (state, { id, patch }) {
      const e = state.expenses.find(x => x.id === id)
      if (e) Object.assign(e, patch)
    },
    REMOVE_EXPENSE (state, id) {
      state.expenses = state.expenses.filter(e => e.id !== id)
    },
    ADD_REPAYMENT (state, repayment) {
      state.repayments.unshift(repayment)
    },
    UPDATE_REPAYMENT (state, { id, patch }) {
      const r = state.repayments.find(x => x.id === id)
      if (r) Object.assign(r, patch)
    },
    SET_BUDGET (state, { tripId, category, amount }) {
      const b = state.budgets.find(x => x.tripId === tripId && x.category === category)
      if (b) b.amount = amount
      else state.budgets.push({ tripId, category, amount })
    },
    SET_RATE (state, { tripId, currency, rate }) {
      const r = state.exchangeRates.find(x => x.tripId === tripId && x.currency === currency)
      if (r) r.rate = rate
      else state.exchangeRates.push({ tripId, currency, rate })
    }
  },
  actions: {
    createExpense ({ commit, getters }, form) {
      // form: { tripId, title, amount, currency, payerId, category, occurredAt, splitMode, memberIds, customShares, note, visibility, createdBy, receiptImage, pending }
      const isForeign = form.currency !== form.settlementCurrency
      if (isForeign && !getters.ratesByTrip(form.tripId).some(r => r.currency === form.currency)) {
        // 第一次使用這個幣別記帳時，先在匯率表建一筆預設 1:1，讓使用者之後可以在財務頁面調整。
        commit('SET_RATE', { tripId: form.tripId, currency: form.currency, rate: 1 })
      }
      const rate = isForeign ? getters.rateFor(form.tripId, form.currency) : 1
      const amountMinor = toMinor(form.amount)
      const settlementMinor = Math.round(amountMinor * rate)
      let shares
      if (form.splitMode === 'none') {
        shares = [{ memberId: form.payerId, amountMinor: settlementMinor }]
      } else if (form.splitMode === 'custom') {
        shares = form.customShares.map(s => ({ memberId: s.memberId, amountMinor: toMinor(s.amount) }))
      } else {
        shares = splitEven(settlementMinor, form.memberIds, form.payerId)
      }
      const id = genId()
      commit('ADD_EXPENSE', {
        id,
        tripId: form.tripId,
        title: form.title,
        amountMinor,
        currency: form.currency,
        rateSnapshot: rate,
        payerId: form.payerId,
        occurredAt: form.occurredAt || new Date().toISOString(),
        category: form.category,
        splitMode: form.splitMode,
        shares,
        visibility: form.visibility || (form.splitMode === 'none' ? 'private' : 'trip'),
        receiptImage: form.receiptImage || null,
        note: form.note || '',
        pending: !!form.pending,
        createdBy: form.createdBy
      })
      return id
    },
    updateExpense ({ commit }, payload) {
      commit('UPDATE_EXPENSE', payload)
    },
    removeExpense ({ commit }, id) {
      commit('REMOVE_EXPENSE', id)
    },
    markRepaid ({ commit }, { tripId, fromId, toId, amountMinor, expenseId }) {
      const id = genId()
      commit('ADD_REPAYMENT', {
        id, tripId, fromId, toId, amountMinor, expenseId: expenseId || null,
        status: 'pending', createdAt: new Date().toISOString()
      })
      return id
    },
    markReceivedDirect ({ commit }, { tripId, fromId, toId, amountMinor, expenseId }) {
      const id = genId()
      commit('ADD_REPAYMENT', {
        id, tripId, fromId, toId, amountMinor, expenseId: expenseId || null,
        status: 'confirmed', createdAt: new Date().toISOString()
      })
      return id
    },
    confirmRepayment ({ commit }, id) {
      commit('UPDATE_REPAYMENT', { id, patch: { status: 'confirmed' } })
    },
    setBudget ({ commit }, payload) {
      commit('SET_BUDGET', payload)
    },
    setRate ({ commit }, payload) {
      commit('SET_RATE', payload)
    }
  }
}
